/**
 * AudioEnginePill — admin cockpit for TTS engine status.
 *
 * Shows current provider, Fish Audio credit balance (live), warm progress,
 * and an emergency toggle to flip the active TTS engine without a redeploy.
 *
 * Polls `/admin/audio/engine` every 10 seconds.
 */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Waveform, Lightning, ArrowsLeftRight, CheckCircle, WarningCircle, Spinner } from "@phosphor-icons/react";

const fmtCredit = (v) => (v == null ? "—" : `$${v.toFixed(2)}`);
const fmtStatus = {
  healthy: { label: "Healthy", color: "text-emerald-400", icon: CheckCircle },
  low:     { label: "Low",     color: "text-amber-400",   icon: WarningCircle },
  empty:   { label: "Empty",   color: "text-red-400",     icon: WarningCircle },
  error:   { label: "Error",   color: "text-red-400",     icon: WarningCircle },
  unknown: { label: "—",       color: "text-exodus-muted", icon: Spinner },
};

export default function AudioEnginePill() {
  const [status, setStatus] = useState(null);
  const [flipping, setFlipping] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get("/admin/audio/engine");
        if (!cancelled) setStatus(r.data);
      } catch (_e) {/* ignore */}
    };
    tick();
    const id = setInterval(tick, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const flip = async () => {
    if (!status) return;
    const next = status.provider === "fish" ? "elevenlabs" : "fish";
    if (!confirm(`Switch TTS engine from ${status.provider} to ${next}?\n\nAll new audio renders will route to ${next} immediately. This persists to .env.`)) return;
    setFlipping(true);
    try {
      await api.post("/admin/audio/provider", { provider: next });
      toast.success(`TTS engine → ${next}`);
      const r = await api.get("/admin/audio/engine");
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provider swap failed");
    } finally {
      setFlipping(false);
    }
  };

  if (!status) {
    return (
      <div className="card-surface p-4 flex items-center gap-3" data-testid="audio-engine-pill-loading">
        <Spinner size={16} className="animate-spin text-exodus-muted" />
        <span className="text-xs text-exodus-muted">Loading audio engine…</span>
      </div>
    );
  }

  const fish = status.fish || {};
  const eleven = status.elevenlabs || {};
  const warm = status.warm || {};
  const stat = fmtStatus[fish.credit_status] || fmtStatus.unknown;
  const StatIcon = stat.icon;

  return (
    <section
      className="card-surface p-5 lg:p-6"
      data-testid="audio-engine-pill"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Waveform size={18} weight="thin" className="text-exodus-gold" />
          <p className="eyebrow">Audio Engine</p>
        </div>
        <button
          data-testid="audio-engine-flip-btn"
          onClick={flip}
          disabled={flipping}
          className="text-[10px] eyebrow tracking-[2px] text-exodus-muted hover:text-exodus-gold transition-colors flex items-center gap-1.5 disabled:opacity-50"
          title={`Switch to ${status.provider === "fish" ? "ElevenLabs" : "Fish Audio"}`}
        >
          {flipping ? (
            <Spinner size={12} className="animate-spin" />
          ) : (
            <ArrowsLeftRight size={12} weight="bold" />
          )}
          Switch engine
        </button>
      </div>

      {/* CURRENT PROVIDER */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Fish Audio card */}
        <div
          className={`border p-4 ${status.provider === "fish" ? "border-exodus-gold" : "border-exodus-border"}`}
          data-testid="engine-fish-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightning size={14} weight="thin" className="text-exodus-gold" />
              <p className="font-display text-base">Fish Audio</p>
            </div>
            {status.provider === "fish" && (
              <span className="text-[9px] eyebrow tracking-[2px] bg-exodus-gold text-exodus-bg px-2 py-0.5">
                ACTIVE
              </span>
            )}
          </div>
          <div className="mt-3 space-y-1.5 text-[11px]">
            <div className="flex justify-between">
              <span className="text-exodus-muted">Credit</span>
              <span className="font-mono text-exodus-text" data-testid="engine-fish-credit">
                {fmtCredit(fish.credit_usd)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-exodus-muted">Status</span>
              <span className={`flex items-center gap-1 ${stat.color}`} data-testid="engine-fish-status">
                <StatIcon size={11} weight="fill" />
                {stat.label}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-exodus-muted">Model</span>
              <span className="text-exodus-text">{fish.model || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-exodus-muted">Voice</span>
              <span className="font-mono text-exodus-muted">{fish.voice_id || "—"}</span>
            </div>
          </div>
          {fish.credit_status === "low" && (
            <p className="text-[10px] text-amber-400 mt-3 leading-relaxed">
              Credit running low. Top up at{" "}
              <a href="https://fish.audio/app/developers" target="_blank" rel="noreferrer" className="underline">
                fish.audio/app/developers
              </a>
            </p>
          )}
          {fish.credit_status === "empty" && (
            <p className="text-[10px] text-red-400 mt-3 leading-relaxed">
              API credit exhausted. Add funds at{" "}
              <a href="https://fish.audio/app/developers" target="_blank" rel="noreferrer" className="underline">
                fish.audio/app/developers
              </a>{" "}
              or switch to ElevenLabs.
            </p>
          )}
          {fish.error && (
            <p className="text-[10px] text-red-400 mt-2 truncate" title={fish.error}>
              {fish.error}
            </p>
          )}
        </div>

        {/* ElevenLabs card */}
        <div
          className={`border p-4 ${status.provider === "elevenlabs" ? "border-exodus-gold" : "border-exodus-border"}`}
          data-testid="engine-eleven-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lightning size={14} weight="thin" className="text-exodus-muted" />
              <p className="font-display text-base text-exodus-muted">ElevenLabs</p>
            </div>
            {status.provider === "elevenlabs" ? (
              <span className="text-[9px] eyebrow tracking-[2px] bg-exodus-gold text-exodus-bg px-2 py-0.5">
                ACTIVE
              </span>
            ) : (
              <span className="text-[9px] eyebrow tracking-[2px] text-exodus-muted">
                FALLBACK
              </span>
            )}
          </div>
          <div className="mt-3 space-y-1.5 text-[11px]">
            <div className="flex justify-between">
              <span className="text-exodus-muted">Configured</span>
              <span className="text-exodus-text">{eleven.configured ? "Yes" : "No"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-exodus-muted">Model</span>
              <span className="text-exodus-text">{eleven.model || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-exodus-muted">Voice</span>
              <span className="font-mono text-exodus-muted">{eleven.voice_id || "—"}</span>
            </div>
          </div>
          <p className="text-[10px] text-exodus-muted mt-3 leading-relaxed">
            Used as silent fallback if Fish Audio returns a billing or credit error.
          </p>
        </div>
      </div>

      {/* WARM PROGRESS */}
      {(warm.running || warm.done > 0) && (
        <div className="mt-4 pt-4 border-t border-exodus-border" data-testid="engine-warm-progress">
          <div className="flex items-center justify-between mb-2">
            <p className="eyebrow text-[10px]">Pre-warm progress</p>
            <span className="text-[11px] text-exodus-gold font-mono">
              {warm.done}/{warm.total}
              {warm.failures > 0 && <span className="text-red-400 ml-2">{warm.failures} fail</span>}
            </span>
          </div>
          <div className="h-1 bg-exodus-border">
            <div
              className="h-1 bg-exodus-gold transition-all"
              style={{ width: warm.total ? `${(warm.done / warm.total) * 100}%` : "0%" }}
            />
          </div>
          {warm.last && (
            <p className="text-[10px] text-exodus-muted mt-2 truncate" title={warm.last}>
              Last: {warm.last}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
